import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { ComicCard } from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const filteredComics = useMemo(() => {
    let list = comics.filter((c) => c.category === currentCategory.id);
    if (sortBy === 'popular') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }
    return list;
  }, [currentCategory.id, sortBy]);

  const sortOptions = [
    { value: 'latest', label: '最新' },
    { value: 'popular', label: '最热' },
    { value: 'rating', label: '评分' },
  ] as const;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="pt-1">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '漫画分类', path: '/category/hot-blood' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        <section className="relative w-full h-[200px] md:h-[280px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20tone%20artistic%20manga%20category%20banner%2C%20soft%20gradient%20orange%20and%20cream%20background%2C%20minimalist%20clean%20design%2C%20no%20text%2C%20modern%20digital%20art&width=1600&height=400&seq=category-hero-generic&orientation=landscape"
              alt={`51漫画${currentCategory.name}分类`}
              title={`51漫画 - ${currentCategory.name}漫画分类`}
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/20"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <h1 className="text-2xl md:text-4xl font-heading text-background-50 font-bold">
              {currentCategory.name}漫画
            </h1>
          </div>
        </section>

        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer ${
                      cat.id === currentCategory.id
                        ? 'bg-accent-500 text-background-50 font-medium'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900 font-medium'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>

        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                {currentCategory.name}漫画推荐
              </h2>
              <p className="text-sm text-foreground-500 max-w-sm">
                精选{currentCategory.name}分类下的优质漫画作品，让你一次看过瘾。
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              <div className="md:col-span-2 bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${currentCategory.icon} text-2xl md:text-3xl text-accent-500`}></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-medium text-foreground-900">关于{currentCategory.name}漫画</h3>
                    <p className="mt-1 text-sm text-foreground-500 leading-relaxed">
                      收录最新最全的{currentCategory.name}类漫画作品，每日更新，免费在线观看。
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-3xl font-bold text-accent-500">{filteredComics.length}</p>
                  <p className="mt-1 text-sm text-foreground-500">部作品</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `51漫画 - ${currentCategory.name}漫画`,
            description: `51漫画${currentCategory.name}分类，收录最新最全${currentCategory.name}类漫画作品，免费在线观看`,
            url: `https://www.51comic.com/category/${currentCategory.slug}`,
            isPartOf: {
              "@type": "WebSite",
              name: "51漫画",
              url: "https://www.51comic.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://www.51comic.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "漫画分类",
                  item: "https://www.51comic.com/category/hot-blood",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: `${currentCategory.name}漫画`,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}